import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { hideLeftSidebar } from '@components/sidebar';

import { usePublishedVersions } from 'api/journeys/hooks';
import { PublishedVersion } from 'api/types';

import { clearPreviewVersionParam, setPreviewVersionParam } from '../journey-edit-helpers';
import { useEditVersionNameModal } from '../modals/edit-version-name';
import {
  useVersionHistoryPanelSidebar,
  VersionHistorySidebarProps,
} from '../sidebars/version-history';

export type UseVersionPreviewOptions = {
  journeyShortId: string;
  hasUnpublishedChanges: boolean;
  basedOnVersionId?: string | null;
};

export type UseVersionPreviewReturn = {
  previewVersion: PublishedVersion | null;
  setPreviewVersion: (version: PublishedVersion | null) => void;
  isPreviewMode: boolean;
  handleSelectVersion: (version: PublishedVersion | null) => void;
  handleExitPreviewMode: () => void;
  handleOpenVersionHistory: () => void;
  handleRestoreVersion: () => void;
};

export const useVersionPreview = ({
  journeyShortId,
  hasUnpublishedChanges,
  basedOnVersionId,
}: UseVersionPreviewOptions): UseVersionPreviewReturn => {
  const [searchParams] = useSearchParams();
  const [previewVersion, setPreviewVersion] = useState<PublishedVersion | null>(null);
  const isPreviewMode = previewVersion !== null;

  const showVersionHistorySidebar = useVersionHistoryPanelSidebar();
  const showEditVersionNameModal = useEditVersionNameModal();

  const hasInitializedFromUrl = useRef(false);

  const previewVersionShortId = searchParams.get('previewVersion');

  const publishedVersionsQuery = usePublishedVersions(journeyShortId, {
    enabled: Boolean(previewVersionShortId),
  });

  const buildSidebarConfig = (
    currentPreviewVersion: PublishedVersion | null,
    overrides?: Partial<VersionHistorySidebarProps>,
  ): VersionHistorySidebarProps => ({
    journeyShortId,
    hasUnpublishedChanges,
    basedOnVersionId,
    previewVersion: currentPreviewVersion,
    onSelectVersion: handleSelectVersion,
    onRenameVersion: (version) => {
      showEditVersionNameModal({ journeyShortId, version });
    },
    onRestoreSuccess: () => {
      setPreviewVersion(null);
      clearPreviewVersionParam();
    },
    ...overrides,
  });

  const handleSelectVersion = (version: PublishedVersion | null) => {
    setPreviewVersion(version);
    if (version) {
      setPreviewVersionParam(version.shortId);
    } else {
      clearPreviewVersionParam();
    }
  };

  const handleExitPreviewMode = () => {
    setPreviewVersion(null);
    clearPreviewVersionParam();
    hideLeftSidebar();
  };

  const handleOpenVersionHistory = () => {
    showVersionHistorySidebar(buildSidebarConfig(previewVersion));
  };

  const handleRestoreVersion = () => {
    if (!previewVersion) return;

    showVersionHistorySidebar(
      buildSidebarConfig(previewVersion, {
        initialView: 'restore',
        initialVersionForAction: {
          versionId: previewVersion.shortId,
          versionNumber: previewVersion.versionNumber,
          dateCreated: previewVersion.dateCreated,
        },
      }),
    );
  };

  useEffect(() => {
    if (hasInitializedFromUrl.current) return;
    if (!previewVersionShortId) return;
    if (!publishedVersionsQuery.data) return;

    const version = publishedVersionsQuery.data.find((v) => v.shortId === previewVersionShortId);

    hasInitializedFromUrl.current = true;
    if (version) {
      setPreviewVersion(version);

      showVersionHistorySidebar(buildSidebarConfig(version));
    } else {
      clearPreviewVersionParam();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [previewVersionShortId, publishedVersionsQuery.data]);

  return {
    previewVersion,
    setPreviewVersion,
    isPreviewMode,
    handleSelectVersion,
    handleExitPreviewMode,
    handleOpenVersionHistory,
    handleRestoreVersion,
  };
};
